import Alert from '@codegouvfr/react-dsfr/Alert';
import Button from '@codegouvfr/react-dsfr/Button';
import ButtonsGroup from '@codegouvfr/react-dsfr/ButtonsGroup';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import clsx from 'clsx';
import { intersection, isNil } from 'lodash-es';
import {
  LegalContext,
  LegalContextLabels,
  LegalContextList
} from 'maestro-shared/referential/LegalContext';
import { Company } from 'maestro-shared/schema/Company/Company';
import { CompanyWorkshop } from 'maestro-shared/schema/Company/CompanyWorkshop';
import {
  Context,
  ContextLabels,
  OutsideProgrammingPlanContext,
  ProgrammingPlanContext
} from 'maestro-shared/schema/ProgrammingPlan/Context';
import { programmingPlanContexts } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlans';
import {
  isPPVSubPlan,
  type ProgrammingSubPlanId,
  stagesFromSubPlans,
  subPlanLabel
} from 'maestro-shared/schema/ProgrammingPlan/ProgrammingSubPlan';
import {
  isOutsideProgrammingPlanSample,
  type PartialSample,
  type PartialSampleToCreate,
  SampleContextData
} from 'maestro-shared/schema/Sample/Sample';
import {
  type SampleStep,
  SampleSteps
} from 'maestro-shared/schema/Sample/SampleStep';
import type { SpecificData } from 'maestro-shared/schema/SpecificData/SpecificData';
import type { Sampler } from 'maestro-shared/schema/User/User';
import {
  UserRoleList,
  UserRolePermissions
} from 'maestro-shared/schema/User/UserRole';
import type React from 'react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import balance from 'src/assets/illustrations/balance.svg';
import check from 'src/assets/illustrations/check.svg';
import controle from 'src/assets/illustrations/controle.svg';
import leaf from 'src/assets/illustrations/leaf.svg';
import warning from 'src/assets/illustrations/warning.svg';
import AppRadioButtons from 'src/components/_app/AppRadioButtons/AppRadioButtons';
import AppRequiredText from 'src/components/_app/AppRequired/AppRequiredText';
import {
  defaultAppSelectOption,
  samplersOptions,
  selectOptionsFromList
} from 'src/components/_app/AppSelect/AppSelectOption';
import AppTextAreaInput from 'src/components/_app/AppTextAreaInput/AppTextAreaInput';
import AppTextInput from 'src/components/_app/AppTextInput/AppTextInput';
import SampleGeolocationForm from 'src/components/Sample/SampleGeolocationForm/SampleGeolocationForm';
import { useForm } from 'src/hooks/useForm';
import { useMatrixReferential } from 'src/hooks/useMatrixReferential';
import { useSamplesLink } from 'src/hooks/useSamplesLink';
import SampleCompany from 'src/views/SampleView/DraftSample/ContextStep/SampleCompany';
import SupportDocumentDownload from 'src/views/SampleView/DraftSample/SupportDocumentDownload';
import { z } from 'zod';
import AppServiceErrorAlert from '../../../../components/_app/AppErrorAlert/AppServiceErrorAlert';
import AppSelect from '../../../../components/_app/AppSelect/AppSelect';
import { useAnalytics } from '../../../../hooks/useAnalytics';
import { useAuthentication } from '../../../../hooks/useAuthentication';
import { usePartialSample } from '../../../../hooks/usePartialSample';
import { ApiClientContext } from '../../../../services/apiClient';
import NextButton from '../NextButton';
import { SampleEmptyFormDownload } from '../SampleEmptyFormDownload';
import { sampleLegalContext } from './sampleLegalContext';

type Props = {
  partialSample?: PartialSample | PartialSampleToCreate;
};

const ContextStep = ({ partialSample }: Props) => {
  const { navigateToSample, navigateToSamples } = useSamplesLink();
  const {
    readonly,
    programmingPlan,
    programmingPlanPrescriptions,
    programmingPlanLocalPrescriptions
  } = usePartialSample(partialSample);
  const { trackEvent } = useAnalytics();
  const { user } = useAuthentication();
  const apiClient = useContext(ApiClientContext);
  const matrixReferential = useMatrixReferential();
  const [resytalId, setResytalId] = useState(
    partialSample?.resytalId ?? undefined
  );
  const planContexts = programmingPlanContexts({
    subPlans: programmingPlan?.subPlans ?? []
  });
  const [context, setContext] = useState<
    ProgrammingPlanContext | 'OutsideProgrammingPlan' | undefined
  >(
    planContexts.length === 1
      ? planContexts[0]
      : isOutsideProgrammingPlanSample(partialSample)
        ? 'OutsideProgrammingPlan'
        : ProgrammingPlanContext.safeParse(partialSample?.context).data
  );
  const [outsideProgrammingPlanContext, setOutsideProgrammingPlanContext] =
    useState<OutsideProgrammingPlanContext | undefined>(
      isOutsideProgrammingPlanSample(partialSample)
        ? OutsideProgrammingPlanContext.safeParse(partialSample?.context).data
        : undefined
    );
  const [programmingSubPlanId, setProgrammingSubPlanId] = useState(
    partialSample?.programmingSubPlanId ?? ''
  );
  const [legalContext, setLegalContext] = useState(partialSample?.legalContext);

  const [geolocationX, setGeolocationX] = useState(
    partialSample?.geolocation?.x ??
      (user?.companies?.length === 1
        ? user?.companies?.[0].geolocation?.x
        : undefined)
  );
  const [geolocationY, setGeolocationY] = useState(
    partialSample?.geolocation?.y ??
      (user?.companies?.length === 1
        ? user?.companies?.[0].geolocation?.y
        : undefined)
  );
  const [company, setCompany] = useState(
    partialSample?.company ??
      (user?.companies?.length === 1 ? user?.companies?.[0] : undefined)
  );
  const [companyWorkshop, setCompanyWorkshop] = useState(
    partialSample?.companyWorkshop ?? undefined
  );
  const { data: companyWorkshops = [] } =
    apiClient.useFindCompanyWorkshopsQuery(
      { siret: company?.siret as string },
      { skip: !company?.siret }
    );
  const [isBrowserGeolocation, setIsBrowserGeolocation] = useState(false);
  const [sampler, setSampler] = useState<Sampler | undefined>(
    partialSample?.sampler ?? user
  );
  const [additionalSampler, setAdditionalSampler] = useState(
    partialSample?.additionalSampler ?? undefined
  );
  const [showAdditionalSampler, setShowAdditionalSampler] = useState(
    !isNil(partialSample?.additionalSampler)
  );

  const [parcel, setParcel] = useState(partialSample?.parcel);
  const [companyOffline] = useState(partialSample?.companyOffline);
  const [notesOnCreation, setNotesOnCreation] = useState(
    partialSample?.notesOnCreation
  );
  const isSubmittingRef = useRef<boolean>(false);

  const [createOrUpdateSample, createOrUpdateSampleCall] =
    apiClient.useCreateOrUpdateSampleMutation();

  useEffect(() => {
    if (programmingPlan?.subPlans.length === 1) {
      setProgrammingSubPlanId(programmingPlan?.subPlans[0]?.id ?? '');
    }
  }, [programmingPlan]);

  const isPPV = useMemo(
    () => programmingPlan?.subPlans.some(isPPVSubPlan) ?? false,
    [programmingPlan]
  );

  const specificData = useMemo(() => {
    const subPlanId = partialSample?.programmingSubPlanId;
    if (programmingSubPlanId !== subPlanId) {
      return {};
    } else {
      return partialSample?.specificData;
    }
  }, [
    programmingSubPlanId,
    partialSample?.programmingSubPlanId,
    partialSample?.specificData
  ]);

  const { data: samplers } = apiClient.useFindUsersQuery({
    region: user?.region,
    roles: UserRoleList.filter((r) => {
      const permissions = UserRolePermissions[r];
      return (
        permissions.includes('createSample') ||
        permissions.includes('updateSample')
      );
    }),
    stages: stagesFromSubPlans(programmingPlan?.subPlans ?? []),
    disabled: false
  });

  const Form = SampleContextData.omit({
    programmingPlanId: true,
    geolocation: true,
    company: true,
    context: true,
    department: true
  })
    .extend({
      geolocationX: z.number({
        error: (issue) => {
          return isNil(issue.input)
            ? 'Veuillez renseigner la latitude.'
            : 'Latitude invalide.';
        }
      }),
      geolocationY: z.number({
        error: (issue) => {
          return isNil(issue.input)
            ? 'Veuillez renseigner la longitude.'
            : 'Latitude invalide.';
        }
      })
    })
    .extend({
      programmingSubPlanId: z
        .string({ error: 'Veuillez renseigner le type de plan.' })
        .brand<'ProgrammingSubPlanId'>(),
      context: z.enum(
        [...ProgrammingPlanContext.options, 'OutsideProgrammingPlan'],
        {
          error: (issue) =>
            isNil(issue.input)
              ? 'Veuillez renseigner le contexte.'
              : issue.message
        }
      ),
      outsideProgrammingPlanContext:
        context === 'OutsideProgrammingPlan' ? Context : z.undefined(),
      legalContext: isPPV ? LegalContext : z.undefined()
    })
    .extend({ company: Company })
    .extend({ companyWorkshop: CompanyWorkshop.nullish() });

  const contextOptions = selectOptionsFromList(
    [...planContexts, 'OutsideProgrammingPlan'],
    {
      labels: {
        ...ContextLabels,
        OutsideProgrammingPlan: 'Hors programmation'
      },
      withDefault: false,
      withSort: false
    }
  );

  const outsideProgrammingPlanContextOptions = selectOptionsFromList(
    OutsideProgrammingPlanContext.options,
    {
      labels: ContextLabels,
      withDefault: true,
      withSort: true
    }
  );

  const ContextIllustrations: Record<
    ProgrammingPlanContext | 'OutsideProgrammingPlan',
    any
  > = {
    Control: check,
    Surveillance: leaf,
    Exploratory: controle,
    OutsideProgrammingPlan: warning
  };

  const legalContextOptions = selectOptionsFromList(LegalContextList, {
    labels: LegalContextLabels,
    withDefault: false
  });

  const programmingSubPlanOptions = selectOptionsFromList(
    intersection(
      programmingPlan?.subPlans
        .filter(
          (subPlan) =>
            context === 'OutsideProgrammingPlan' ||
            programmingPlanPrescriptions?.some(
              (prescription) =>
                prescription.programmingSubPlanId === subPlan.id &&
                programmingPlanLocalPrescriptions?.some(
                  (localPrescription) =>
                    localPrescription.prescriptionId === prescription.id &&
                    localPrescription.sampleCount > 0
                )
            )
        )
        .map((sp) => sp.id),
      user?.programmingSubPlans?.map((sp) => sp.id) ?? []
    ),
    {
      labels: Object.fromEntries(
        (programmingPlan?.subPlans ?? []).map((sp) => [
          sp.id,
          subPlanLabel(matrixReferential, sp)
        ])
      ),
      withDefault: 'auto',
      withSort: true
    }
  );

  const id = useMemo(
    () => partialSample?.id ?? crypto.randomUUID(),
    [partialSample]
  );

  const formData = {
    id,
    sampler: sampler as Sampler,
    additionalSampler: showAdditionalSampler ? additionalSampler : undefined,
    geolocation:
      geolocationX && geolocationY
        ? {
            x: geolocationX as number,
            y: geolocationY as number
          }
        : undefined,
    parcel,
    programmingPlanId: programmingPlan?.id as string,
    programmingSubPlanId: (programmingSubPlanId || undefined) as
      | ProgrammingSubPlanId
      | undefined,
    context:
      context === 'OutsideProgrammingPlan'
        ? outsideProgrammingPlanContext
        : context,
    legalContext: sampleLegalContext({ isPPV, context, legalContext }),
    company,
    companyWorkshop,
    companyOffline,
    resytalId: resytalId || undefined,
    notesOnCreation,
    step: 'Draft' as const,
    status: 'Draft' as const,
    specificData: specificData as SpecificData
  };

  useEffect(
    () => {
      if (isSubmittingRef.current && !createOrUpdateSampleCall.isLoading) {
        isSubmittingRef.current = false;

        if (createOrUpdateSampleCall.isSuccess) {
          trackEvent('sample', `submit_${formData.status}`, formData.id);
          navigateToSample(formData.id, 2);
        }
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      createOrUpdateSampleCall.isSuccess,
      createOrUpdateSampleCall.isLoading,
      formData.id
    ]
  );

  const submit = async (e?: React.MouseEvent<HTMLElement>) => {
    e?.preventDefault();
    if (!isSubmittingRef.current) {
      await form.validate(async () => {
        isSubmittingRef.current = true;
        await createOrUpdateSample({
          ...partialSample,
          ...formData,
          status: 'Draft',
          step: 'DraftMatrix'
        });
      });
    }
  };

  const save = async (step = partialSample?.step) => {
    if (partialSample) {
      createOrUpdateSample({
        ...partialSample,
        ...formData,
        step: step as SampleStep
      });
    }
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        if (!geolocationX && !geolocationY) {
          setGeolocationX(position.coords.latitude);
          setGeolocationY(position.coords.longitude);
        }
        setIsBrowserGeolocation(true);
        trackEvent('geolocation', 'enable');
      });
    } else {
      setIsBrowserGeolocation(false);
      trackEvent('geolocation', 'disable');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formInput = {
    id,
    sampler,
    additionalSampler,
    geolocationX,
    geolocationY,
    parcel,
    programmingSubPlanId: (programmingSubPlanId || undefined) as
      | ProgrammingSubPlanId
      | undefined,
    context,
    outsideProgrammingPlanContext,
    legalContext: isPPV ? legalContext : undefined,
    company,
    companyWorkshop,
    companyOffline,
    resytalId: resytalId || undefined,
    notesOnCreation,
    step: 'DraftMatrix',
    status: 'Draft',
    specificData
  };

  const form = useForm(Form, formInput, save);

  if (!programmingPlan) {
    return null;
  }
  return (
    <form data-testid="draft_sample_creation_form" className="sample-form">
      {isPPV && !isBrowserGeolocation && !readonly && (
        <Alert
          severity="info"
          title=""
          small
          closable
          description={`Autorisez le partage de votre position pour faciliter la localisation 
            ${isPPV ? ' de la parcelle' : ' du contrôle'}.`}
        />
      )}
      <div>
        {partialSample &&
          (!readonly || SampleSteps[partialSample.step] > 1) && (
            <div className={clsx(cx('fr-mb-1v'), 'd-flex-align-center')}>
              <div className={clsx('flex-grow-1')} />
              <Button
                size="small"
                priority="tertiary no outline"
                className={clsx(cx('fr-pr-0'))}
                iconId="fr-icon-arrow-right-line"
                iconPosition="right"
                onClick={async (e) =>
                  readonly ? navigateToSample(partialSample.id, 2) : submit(e)
                }
              >
                Étape suivante
              </Button>
            </div>
          )}
        <AppRequiredText />
      </div>
      {isPPV && (
        <div className={cx('fr-grid-row', 'fr-grid-row--gutters')}>
          <div className={cx('fr-col-12')}>
            <div className={clsx('d-flex-align-start')}>
              <AppSelect
                value={sampler?.id || ''}
                options={samplersOptions(samplers, user?.id)}
                onChange={(e) =>
                  setSampler(
                    samplers?.find((sampler) => sampler.id === e.target.value)
                  )
                }
                inputForm={form}
                inputKey="sampler"
                whenValid="Préleveur correctement renseigné."
                label="Le préleveur"
                hint="La personne qui réalise le prélevement"
                required
                disabled={readonly}
                className={clsx('flex-grow-1')}
              />
              {!showAdditionalSampler && !readonly && (
                <Button
                  priority="tertiary"
                  iconId="fr-icon-user-add-line"
                  onClick={() => setShowAdditionalSampler(true)}
                  className={cx('fr-mt-7w', 'fr-ml-2w')}
                >
                  Ajouter
                </Button>
              )}
            </div>
          </div>
          {showAdditionalSampler && (
            <div className={cx('fr-col-12')}>
              <div className={clsx('d-flex-align-start')}>
                <AppSelect
                  value={additionalSampler?.id || ''}
                  options={[
                    defaultAppSelectOption('Sélectionner un préleveur'),
                    ...samplersOptions(samplers).filter(
                      (s) => s.value !== sampler?.id
                    )
                  ]}
                  onChange={(e) =>
                    setAdditionalSampler(
                      samplers?.find((sampler) => sampler.id === e.target.value)
                    )
                  }
                  inputForm={form}
                  inputKey="additionalSampler"
                  whenValid="Préleveur secondaire correctement renseigné."
                  label="Préleveur secondaire"
                  disabled={readonly}
                  className={clsx('flex-grow-1')}
                  required
                />
                {!readonly && (
                  <Button
                    priority="tertiary"
                    onClick={() => {
                      setShowAdditionalSampler(false);
                      setAdditionalSampler(undefined);
                    }}
                    title="Supprimer"
                    className={cx('fr-mt-4w', 'fr-ml-2w')}
                  >
                    Supprimer
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {isPPV && (
        <SampleGeolocationForm
          key={`geolocation-${isBrowserGeolocation}`}
          title="Emplacement de la parcelle contrôlée"
          geolocationX={geolocationX}
          geolocationY={geolocationY}
          onChangeLocation={(location) => {
            setGeolocationX(location.x);
            setGeolocationY(location.y);
          }}
          onChangeGeolocationX={setGeolocationX}
          onChangeGeolocationY={setGeolocationY}
          inputForm={form}
          readonly={readonly}
        >
          <div className={cx('fr-col-12')}>
            <AppTextInput
              defaultValue={parcel ?? ''}
              onChange={(e) => setParcel(e.target.value)}
              inputForm={form}
              inputKey="parcel"
              whenValid="Parcelle correctement renseignée."
              data-testid="parcel-input"
              label="N° ou appellation de la parcelle"
              hintText="Facultatif"
              disabled={readonly}
            />
          </div>
        </SampleGeolocationForm>
      )}
      {programmingPlan.subPlans.length > 1 && (
        <AppSelect
          value={programmingSubPlanId}
          options={programmingSubPlanOptions}
          onChange={(e) => setProgrammingSubPlanId(e.target.value)}
          inputForm={form}
          inputKey="programmingSubPlanId"
          whenValid="Type de plan correctement renseigné."
          data-testid="programmingSubPlan-select"
          label="Type de plan"
          disabled={readonly}
          required
          className={cx('fr-mb-0')}
        />
      )}
      {contextOptions.length > 1 && (
        <AppRadioButtons
          legend="Contexte du prélèvement"
          options={[
            ...contextOptions.map(({ label, value }) => ({
              key: `context-option-${value}`,
              label,
              nativeInputProps: {
                checked: context === value,
                onChange: () => {
                  setContext(
                    value as ProgrammingPlanContext | 'OutsideProgrammingPlan'
                  );
                  if (value !== 'OutsideProgrammingPlan') {
                    setOutsideProgrammingPlanContext(undefined);
                  }
                }
              },
              illustration: (
                <img
                  src={
                    ContextIllustrations[
                      value as ProgrammingPlanContext | 'OutsideProgrammingPlan'
                    ]
                  }
                  alt=""
                  aria-hidden
                />
              )
            }))
          ]}
          colSm={6}
          inputForm={form}
          inputKey="context"
          whenValid="Contexte du prélèvement correctement renseigné."
          required
          disabled={readonly}
          data-testid="context-radio"
        />
      )}
      {context === 'OutsideProgrammingPlan' && (
        <div className={cx('fr-col-12', 'fr-col-sm-6')}>
          <AppSelect
            defaultValue={outsideProgrammingPlanContext}
            options={outsideProgrammingPlanContextOptions}
            onChange={(e) =>
              setOutsideProgrammingPlanContext(
                e.target.value as OutsideProgrammingPlanContext
              )
            }
            inputForm={form}
            inputKey="outsideProgrammingPlanContext"
            whenValid="Précision du contexte du prélèvement correctement renseignée."
            label="Précision du contexte hors programmation"
            disabled={readonly}
            required
            className={cx('fr-mb-0')}
          />
        </div>
      )}
      {isPPV && (
        <div className={cx('fr-grid-row', 'fr-grid-row--gutters')}>
          <div className={cx('fr-col-12')}>
            <AppRadioButtons
              legend="Cadre juridique"
              options={
                legalContextOptions?.map(({ label, value }) => ({
                  key: `legalContext-option-${value}`,
                  label,
                  nativeInputProps: {
                    checked: legalContext === value,
                    onChange: () => setLegalContext(value as LegalContext)
                  },
                  illustration: (
                    <img
                      src={value === 'B' ? controle : balance}
                      alt=""
                      aria-hidden
                    />
                  )
                })) ?? []
              }
              colSm={6}
              inputForm={form}
              inputKey="legalContext"
              whenValid="Cadre juridique correctement renseigné."
              required
              disabled={readonly}
              data-testid="legalContext-radio"
            />
          </div>
        </div>
      )}
      {isPPV && (
        <div className={cx('fr-grid-row', 'fr-grid-row--gutters')}>
          <div className={cx('fr-col-12')}>
            <AppTextInput
              type="text"
              defaultValue={(partialSample?.resytalId ?? '') as string}
              onChange={(e) => setResytalId(e.target.value)}
              inputForm={form}
              inputKey="resytalId"
              whenValid="Identifiant Resytal correctement renseigné."
              data-testid="resytalId-input"
              label="Identifiant Resytal"
              disabled={readonly}
              hintText="Format AA-XXXXXX"
            />
          </div>
        </div>
      )}

      <SampleCompany
        programmingPlan={programmingPlan}
        partialSample={partialSample}
        programmingSubPlanId={programmingSubPlanId as ProgrammingSubPlanId}
        company={company}
        companyOffline={companyOffline ?? undefined}
        companyWorkshop={companyWorkshop}
        companyWorkshops={companyWorkshops}
        readonly={readonly}
        form={form}
        onCompanyChange={(selected) => {
          setCompany(selected);
          setCompanyWorkshop(undefined);
        }}
        onCompanyWorkshopChange={setCompanyWorkshop}
        onGeolocationChange={(x, y) => {
          setGeolocationX(x);
          setGeolocationY(y);
        }}
      />

      {!!programmingSubPlanId && !isPPV && !!company && !readonly && (
        <SampleEmptyFormDownload partialSample={partialSample ?? formData} />
      )}

      <div className={cx('fr-grid-row', 'fr-grid-row--gutters')}>
        <div className={cx('fr-col-12')}>
          <AppTextAreaInput
            defaultValue={notesOnCreation ?? ''}
            onChange={(e) => setNotesOnCreation(e.target.value)}
            inputForm={form}
            inputKey="notesOnCreation"
            whenValid="Note correctement renseignée."
            data-testid="notes-input"
            label="Note additionnelle"
            hintText="Champ facultatif pour identification et qualité de la personne présente lors du contrôle"
            disabled={readonly}
          />
        </div>
      </div>
      <hr className={cx('fr-mx-0')} />
      <div className={cx('fr-grid-row', 'fr-grid-row--gutters')}>
        <div className={clsx(cx('fr-col-12'), 'sample-actions')}>
          {!readonly ? (
            <>
              <AppServiceErrorAlert call={createOrUpdateSampleCall} />
              <ButtonsGroup
                alignment="between"
                inlineLayoutWhen="md and up"
                buttons={[
                  {
                    children: 'Abandonner la saisie',
                    priority: 'tertiary',
                    onClick: () => navigateToSamples(programmingPlan.year),
                    nativeButtonProps: {
                      'data-testid': 'cancel-button'
                    }
                  },
                  {
                    children: 'Continuer',
                    onClick: submit,
                    iconId: 'fr-icon-arrow-right-line',
                    iconPosition: 'right',
                    disabled: createOrUpdateSampleCall.isLoading,
                    nativeButtonProps: {
                      'data-testid': 'submit-button'
                    }
                  }
                ]}
              />
            </>
          ) : (
            <ul
              className={cx(
                'fr-btns-group',
                'fr-btns-group--inline-md',
                'fr-btns-group--right',
                'fr-btns-group--icon-left'
              )}
            >
              <li>
                <NextButton partialSample={partialSample} currentStep={1} />
              </li>
            </ul>
          )}
        </div>
        {!readonly && programmingSubPlanId && (
          <SupportDocumentDownload partialSample={partialSample ?? formData} />
        )}
      </div>
    </form>
  );
};

export default ContextStep;
